import type { Theme } from "@glideapps/glide-data-grid";

/* -----------------------------------------------------------------------------
 * Volca's palette, resolved for a canvas.
 *
 * The grid is drawn with `@glideapps/glide-data-grid`, which paints to a
 * `<canvas>`. Canvas `fillStyle` cannot resolve a CSS custom property, so the
 * usual `hsl(var(--green-50))` this codebase uses in the DOM is not available
 * here — Glide needs a literal color string.
 *
 * So rather than copy hex values out of `index.css` (which is how this file's
 * colors drifted onto Tailwind's stock palette in the first place), we read the
 * live custom properties off `:root` once and convert them to hex. The tokens
 * stay the single source of truth, and a palette change in `index.css` moves the
 * spreadsheet with the rest of the app.
 *
 * Hex specifically, not `hsl(...)`: `color-contrast.ts` parses these same values
 * to decide what ink a marker needs on an arbitrary cell fill, and its parser
 * handles hex and rgb. Emitting hex keeps that path working untouched.
 * -------------------------------------------------------------------------- */

/**
 * Fallbacks are only reached when no stylesheet is attached — unit tests run in
 * jsdom without `index.css`. They mirror `index.css` so a test never asserts
 * against a color the app would not actually paint; the running app always takes
 * the resolved value instead.
 */
const TOKEN_FALLBACKS: Record<string, string> = {
  // Semantic tokens. `index.css` writes these as `var()` chains onto the ramp
  // (`--stroke-tertiary: var(--gray-15)`), which the browser resolves before
  // `getComputedStyle` hands the value back — so these are what the app paints.
  // The fallbacks below are the resolved end of each chain, which keeps the
  // rendered colour correct even if a browser ever returned the chain unresolved.
  "--text-primary": "#141313", // --gray-100
  "--text-secondary": "#424141", // --gray-80
  "--text-tertiary": "#6F6F6F", // --gray-60
  "--text-quaternary": "#9D9D9D", // --gray-40
  "--surface-primary": "#FFFFFF", // --gray-0
  "--surface-secondary": "#F2F2F2", // --gray-5
  "--stroke-secondary": "#B3B3B3", // --gray-30
  "--stroke-tertiary": "#D7D7D7", // --gray-15

  "--gray-0": "#FFFFFF",
  "--gray-5": "#F2F2F2",
  "--gray-10": "#E5E5E5",
  "--gray-15": "#D7D7D7",
  "--gray-20": "#CACACA",
  "--gray-60": "#6F6F6F",
  "--gray-80": "#424141",
  "--gray-100": "#141313",
  "--green-5": "#EBFDF9",
  "--green-10": "#D4F6EE",
  "--green-20": "#B4E9DC",
  "--green-50": "#00A17A",
  "--green-60": "#008263",
  "--green-70": "#00684F",
  "--green-100": "#034032",
  "--aqua-50": "#009DAC",
  "--aqua-60": "#007F8B",
  "--blue-0": "#FAFDFF",
  "--blue-50": "#008CFF",
  "--blue-60": "#0077D9",
  "--gold-5": "#FFF3CA",
  "--gold-50": "#DC9710",
  "--red-5": "#FFF2F0",
  "--red-70": "#B21D12",
};

const toHexChannel = (channel: number): string =>
  Math.round(channel * 255)
    .toString(16)
    .padStart(2, "0")
    .toUpperCase();

/**
 * Converts the `H S% L%` triple `index.css` stores into hex. Tokens are written
 * unwrapped (`--green-50: 165.5 100% 31.6%`) so Tailwind can compose them with
 * an alpha channel, which means we get the raw triple back and convert it here.
 */
export const hslTripleToHex = (triple: string): string | null => {
  const parts = triple
    .trim()
    .split(/[\s,/]+/)
    .filter(Boolean)
    .slice(0, 3);
  if (parts.length < 3) return null;

  const [h, s, l] = parts.map((part) => Number.parseFloat(part));
  if (h == null || s == null || l == null) return null;
  if ([h, s, l].some(Number.isNaN)) return null;

  const saturation = s / 100;
  const lightness = l / 100;
  const chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
  const huePrime = (((h % 360) + 360) % 360) / 60;
  const second = chroma * (1 - Math.abs((huePrime % 2) - 1));

  const [r, g, b] = (
    [
      [chroma, second, 0],
      [second, chroma, 0],
      [0, chroma, second],
      [0, second, chroma],
      [second, 0, chroma],
      [chroma, 0, second],
    ] as const
  )[Math.floor(huePrime) % 6] ?? [0, 0, 0];

  const match = lightness - chroma / 2;
  return `#${toHexChannel(r + match)}${toHexChannel(g + match)}${toHexChannel(b + match)}`;
};

const resolvedTokens = new Map<string, string>();

/**
 * The hex for a Volca color token, e.g. `volcaColor("--green-50")`.
 *
 * Memoized: the grid asks for these inside per-cell draw callbacks that run on
 * every frame, and `getComputedStyle` forces style resolution.
 */
export const volcaColor = (token: string): string => {
  const cached = resolvedTokens.get(token);
  if (cached !== undefined) return cached;

  const fallback = TOKEN_FALLBACKS[token] ?? "#000000";
  const resolved =
    typeof window === "undefined"
      ? null
      : hslTripleToHex(
          window
            .getComputedStyle(document.documentElement)
            .getPropertyValue(token),
        );

  const value = resolved ?? fallback;
  resolvedTokens.set(token, value);
  return value;
};

/** Clears the memo. Exported for tests that swap the stylesheet under us. */
export const clearVolcaColorCache = (): void => resolvedTokens.clear();

/** A token at partial opacity, for the fills Glide expects to be translucent. */
export const volcaColorAlpha = (token: string, alpha: number): string => {
  const hex = volcaColor(token).slice(1);
  const [r, g, b] = [0, 2, 4].map((i) =>
    Number.parseInt(hex.slice(i, i + 2), 16),
  );
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/**
 * Volca's palette expressed as a Glide theme.
 *
 * Without this the grid renders in Glide's stock theme — an indigo `#4F5DFF`
 * accent on every selection ring and fill handle, and its own greys.
 *
 * The accent is BLUE, not the brand colour. Volca's palette splits three ways:
 * orange is the brand (tab underlines, `--stroke-tab-selected`), green is
 * interactive (buttons, `--primary`), and blue is the selected/hover highlight
 * for rows and lists — `table.tsx` uses `hover:bg-blue-5` and
 * `data-[state=selected]:bg-blue-5`. A selected cell is that third thing, so it
 * takes the same blue rather than the brand or the action colour.
 *
 * `accentLight` stays translucent rather than a flat `--blue-5`: over a white
 * cell 10% blue lands within ~5/255 of `--blue-5`, so a selected cell matches a
 * selected table row, but over the coloured fills a generated payroll sheet
 * paints, it tints instead of erasing them.
 *
 * Type is pinned rather than inherited. Every size below already matched Volca's
 * scale, but by coincidence — they were Glide's defaults, and a Glide upgrade
 * could move the whole grid off the scale without anything in this repo
 * changing. Naming them makes them Volca's decision.
 */
export const buildSpreadsheetTheme = (): Partial<Theme> => ({
  accentColor: volcaColor("--blue-60"),
  accentFg: volcaColor("--gray-0"),
  accentLight: volcaColorAlpha("--blue-50", 0.1),

  // Semantic tokens wherever one exists, so the grid follows what the app means
  // by "primary text" or "a border" rather than a ramp step that happens to
  // match it today. The grid's own DOM chrome already does this — the formula
  // bar's dividers are `border-stroke-tertiary`.
  textDark: volcaColor("--text-primary"),
  textMedium: volcaColor("--text-tertiary"),
  textLight: volcaColor("--text-quaternary"),
  textBubble: volcaColor("--text-primary"),
  textHeader: volcaColor("--text-secondary"),
  textGroupHeader: volcaColor("--text-tertiary"),
  // Glide assumes a selected header is filled with the accent and so wants
  // inverse ink here. `drawSpreadsheetHeader` repaints that fill grey instead,
  // so the title stays normal ink — see the comment there.
  textHeaderSelected: volcaColor("--text-primary"),

  bgIconHeader: volcaColor("--text-tertiary"),
  fgIconHeader: volcaColor("--gray-0"),

  bgCell: volcaColor("--surface-primary"),
  bgCellMedium: volcaColor("--surface-secondary"),

  // Header states climb the grey ramp: at rest, hovered, then holding the
  // selected cell. These three are interaction states with no semantic token of
  // their own, so they name ramp steps directly. `--gray-20` continues the same
  // progression for a fully selected column (`drawSpreadsheetHeader`).
  bgHeader: volcaColor("--surface-secondary"),
  bgHeaderHovered: volcaColor("--gray-10"),
  bgHeaderHasFocus: volcaColor("--gray-15"),

  bgBubble: volcaColor("--surface-secondary"),
  bgBubbleSelected: volcaColor("--surface-primary"),
  bgSearchResult: volcaColor("--gold-5"),

  borderColor: volcaColor("--stroke-tertiary"),
  linkColor: volcaColor("--blue-60"),

  /* --- Type sizes, pinned to Volca's scale -------------------------------
   * `tailwind.config.js` is the source; these are its px values, since a canvas
   * cannot read a Tailwind class any more than it can read a CSS variable.
   * Every size below already rendered this way — they were Glide's defaults and
   * happened to match. Naming them stops a Glide upgrade moving the grid's type
   * off the scale without anything in this repo changing.
   *
   * `fontFamily` is deliberately NOT set here. The canvas therefore keeps
   * rendering in Glide's default stack, which begins with Inter, while the DOM
   * chrome around it is the app's Montserrat. Whether that split is intended is
   * an open question with Volca — so this file does not assert an answer.
   */
  // 13px = `md`. Cells, and the overlay editor, so an open editor matches the
  // cell underneath it.
  baseFontStyle: "13px",
  editorFontSize: "13px",
  // `600 13px` = `subtitleW3`, the semibold body step.
  headerFontStyle: "600 13px",
  // The one knob that was genuinely off: Glide ships 9px row markers, 2px under
  // `sm` (11px), the floor of Volca's scale — nothing else in the product is
  // set that small. The gutter is pinned at 36px and a 4-digit row number
  // measures ~24px at this size, so there is room to spare.
  markerFontStyle: "11px",
});
